import type { ReactNode } from "react";
import { classNames } from "../styles/classNames";
import { suggestionOptionId } from "./suggestionNavigation";

interface SuggestionListProps<T> {
  /** The list's ID, for the field's `aria-controls`; option IDs derive from it. */
  id: string;
  label: string;
  items: readonly T[];
  activeIndex: number;
  itemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  onChoose: (item: T) => void;
  /** Where the list sits beside its field: floating above or below it, or in place inside a panel. */
  placement: "above" | "below" | "inPanel";
  /** The heading an item is listed under; a heading shows where it changes. */
  groupOf?: (item: T) => string;
  /** An item shown but not chosen, such as one that falls short. */
  isDisabled?: (item: T) => boolean;
}

const FLOATING_CLASS = "app-dropdown-panel absolute left-0 z-20 max-h-48 w-64 overflow-y-auto p-1";
const PLACEMENT_CLASS: Readonly<Record<SuggestionListProps<unknown>["placement"], string>> = {
  above: classNames(FLOATING_CLASS, "bottom-full mb-1"),
  below: classNames(FLOATING_CLASS, "top-full mt-1"),
  inPanel: "max-h-72 overflow-y-auto",
};

/**
 * The suggestions for what is typed in a field (WAI-ARIA APG "Combobox", list popup). The
 * field keeps the focus and names the active option with `suggestionOptionId`; Up and Down
 * move with `nextSuggestionIndex`. A pointer chooses without taking the focus.
 */
export default function SuggestionList<T>({ id, label, items, activeIndex, itemKey, renderItem, onChoose, placement, groupOf, isDisabled }: SuggestionListProps<T>) {
  return (
    <ul id={id} role="listbox" aria-label={label} className={PLACEMENT_CLASS[placement]}>
      {items.map((item, index) => {
        const group = groupOf?.(item);
        const previous = index > 0 ? items[index - 1] : undefined;
        const heading = group !== undefined && (previous === undefined || groupOf?.(previous) !== group) ? group : null;
        const disabled = isDisabled?.(item) ?? false;
        return [
          heading === null ? null : (
            <li key={`${itemKey(item)}-group`} role="presentation" className="app-help-text px-2 pb-0.5 pt-2 text-xs font-semibold">
              {heading}
            </li>
          ),
          <li
            key={itemKey(item)}
            id={suggestionOptionId(id, index)}
            role="option"
            aria-selected={index === activeIndex}
            aria-disabled={disabled || undefined}
            className={classNames("app-nav-item px-2 py-1 text-sm", disabled ? "cursor-default opacity-60" : "cursor-pointer", index === activeIndex && "app-nav-item-active")}
            onMouseDown={(event) => {
              event.preventDefault();
              if (!disabled) onChoose(item);
            }}
          >
            {renderItem(item)}
          </li>,
        ];
      })}
    </ul>
  );
}
